import { useEffect, useRef } from "react";
import Modal from "@timeedit/ui-components/antd/modal/index";
import theme from "@timeedit/ui-components/antd/theme/index";
import { Error as ErrorIcon } from "@timeedit/ui-components/components/Icon/Icons/index";
import Language from "../lib/Language";
import Log, { isLogIgnored, TLogEvent } from "../lib/Log";
import { TReferenceItem } from "../types/api/shared";

const ErrorModalIcon = () => {
    const { token } = theme.useToken();

    return (
        <ErrorIcon
            className="anticon"
            // Ensure correct icon size.
            size={Math.round(token.fontSize * token.lineHeight)}
            alt={Language.get("error_title")}
        />
    );
};

type TErrorModalContentProps = {
    message: string;
    details?: TReferenceItem[];
};
const ErrorModalContent = ({ message, details }: TErrorModalContentProps) => (
    <>
        <p>{message}</p>
        {details?.length ? (
            <ul>
                {details.map((detail, index) => (
                    <li key={index}>{detail.label}</li>
                ))}
            </ul>
        ) : null}
    </>
);

/**
 * Listens for log events on the non-recoverable levels and presents them in a blocking modal
 */
const NonRecoverableErrorModal = () => {
    const [modal, contextHolder] = Modal.useModal();
    const isShowingError = useRef(false);

    useEffect(() => {
        const listener = ({ context, message }: TLogEvent) => {
            if (isLogIgnored(context)) {
                return;
            }

            // The app is already unusable once the first of these arrives, so later ones only add
            // more modals to click through - and the first one is the likeliest to name the cause.
            if (isShowingError.current) {
                return;
            }
            isShowingError.current = true;

            const [confirmAction] = context?.actions ?? [];

            modal.error({
                icon: <ErrorModalIcon />,
                // TODO: DEV-15054 Proper strings are defined once te-server
                // provides the error codes this modal is meant to describe.
                title: Language.tmp("Something went wrong"),
                content: <ErrorModalContent message={message} details={context?.details} />,
                okText: confirmAction?.title ?? Language.get("dialog_ok"),
                onOk: confirmAction?.action,
                afterClose: () => {
                    isShowingError.current = false;
                },
                closable: false,
                maskClosable: false,
                keyboard: false,
                centered: true,
            });
        };

        return Log.listen(listener, Log.NON_RECOVERABLE_LEVELS);
    }, [modal]);

    return <>{contextHolder}</>;
};

export default NonRecoverableErrorModal;
